import React from 'react';
import type { SensorUomResponse } from '../../Services/serviceURL';
import {
  UIParameter,
  applyBusinessFriendlyNameChange,
  applyParameterUnitsChange,
  getSaveAssetTitle,
  parameterRowKey,
} from './createPlantDetailsModel';

const UNITS_SELECT_STYLE: React.CSSProperties = {
  width: '100%',
  color: 'white',
  height: '39px',
  borderRadius: '5px',
};

interface ParameterUnitsCellProps {
  param: UIParameter;
  index: number;
  parameters: UIParameter[];
  setParameters: React.Dispatch<React.SetStateAction<UIParameter[]>>;
}

const ParameterUnitsCell: React.FC<ParameterUnitsCellProps> = ({
  param,
  index,
  parameters,
  setParameters,
}) => {
  const handleUnitsChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setParameters(applyParameterUnitsChange(parameters, index, event.target.value));
  };

  if (param.isUserConfigurable === true) {
    return (
      <select value={param.units} onChange={handleUnitsChange} style={UNITS_SELECT_STYLE}>
        <option value="">Select</option>
        {param.unitOptions.map((unit) => (
          <option key={unit.code} value={unit.code}>
            {unit.code}
          </option>
        ))}
      </select>
    );
  }

  if (param.hasUom) {
    const originalUnits = Array.isArray(param.originalUnits) ? param.originalUnits : [];
    return (
      <select value={param.units} onChange={handleUnitsChange} style={UNITS_SELECT_STYLE}>
        <option value="">Select</option>
        {originalUnits.map((unit) => (
          <option key={unit.code} value={unit.code}>
            {unit.code}
          </option>
        ))}
      </select>
    );
  }

  return <span className="parameter-units-empty" aria-hidden="true" />;
};

export interface CreatePlantDetailsParametersTableProps {
  parameters: UIParameter[];
  setParameters: React.Dispatch<React.SetStateAction<UIParameter[]>>;
  validUoms: SensorUomResponse[];
  saveDisabled: boolean;
  onSave: () => void;
}

export const CreatePlantDetailsParametersTable: React.FC<CreatePlantDetailsParametersTableProps> = ({
  parameters,
  setParameters,
  validUoms,
  saveDisabled,
  onSave,
}) => {
  const rows = Array.isArray(parameters) ? parameters : [];

  return (
    <div className="parameters-table">
      <table className="global-table">
        <thead>
          <tr>
            <th className="col-large">Sensor</th>
            <th className="col-medium">Position</th>
            <th className="col-extra-large">Business Friendly Name</th>
            <th className="col-medium">Units</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((param, index) => (
            <tr key={parameterRowKey(param)}>
              <td className="text-wrap">{param.sensor}</td>
              <td className="text-wrap">{param.position || '-'}</td>
              <td>
                <input
                  type="text"
                  value={param.businessFriendlyName}
                  placeholder="Enter here"
                  onChange={(event) => {
                    setParameters(
                      applyBusinessFriendlyNameChange(
                        parameters,
                        index,
                        event.target.value,
                        param,
                        validUoms
                      )
                    );
                  }}
                  style={{ width: '90%', maxWidth: 'none' }}
                />
              </td>
              <td>
                <ParameterUnitsCell
                  param={param}
                  index={index}
                  parameters={parameters}
                  setParameters={setParameters}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button
        type="button"
        className={`save-btn ${saveDisabled ? 'disabled' : ''}`}
        onClick={onSave}
        disabled={saveDisabled}
        title={getSaveAssetTitle(saveDisabled)}
      >
        Save
      </button>
    </div>
  );
};
